"use client";

import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import {
  type AuthStatus,
  type TraderActivationStatus,
  useAuthStore,
} from "@/auth/authStore";
import { useOnboardingStore } from "@/auth/onboarding/onboardingStore";
import { clearRiseSession } from "@/auth/session";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";

import { ConnectedWalletIndicator } from "./ConnectedWalletIndicator";

const getButtonLabel = (
  walletConnecting: boolean,
  authStatus: AuthStatus,
  activationStatus: TraderActivationStatus
): string => {
  if (walletConnecting || authStatus === "connecting") return "Connecting...";
  if (authStatus === "signing") return "Sign message in wallet";
  if (activationStatus === "checking") return "Checking account...";
  if (activationStatus === "notActivated") return "Set up account";
  if (activationStatus === "activated") return "Open dashboard";
  return "Connect wallet";
};

export const LandingConnect = () => {
  const wallet = useWallet();
  const { setVisible } = useWalletModal();
  const router = useRouter();
  const [disconnecting, setDisconnecting] = useState(false);

  const activationError = useAuthStore((state) => state.activationError);
  const activationStatus = useAuthStore((state) => state.activationStatus);
  const authError = useAuthStore((state) => state.error);
  const authStatus = useAuthStore((state) => state.status);
  const authority = useAuthStore((state) => state.authority);
  const startWithAccessCodeEntry = useOnboardingStore(
    (state) => state.startWithAccessCodeEntry
  );

  useEffect(() => {
    if (authStatus === "authenticated" && activationStatus === "activated") {
      router.replace("/dashboard");
    }
  }, [activationStatus, authStatus, router]);

  const openWalletSelector = async (): Promise<void> => {
    setDisconnecting(true);
    try {
      if (wallet.wallet !== null || wallet.connected) {
        await clearRiseSession();
        try {
          await wallet.disconnect();
        } catch {
          // Continue clearing local adapter selection so the next modal click
          // starts from the wallet the user actually chooses.
        }
        wallet.select(null);
        useAuthStore.getState().setUnauthenticated();
      }
    } finally {
      setDisconnecting(false);
      setVisible(true);
    }
  };

  const handleClick = () => {
    if (activationStatus === "activated") {
      router.replace("/dashboard");
      return;
    }

    if (activationStatus === "notActivated" && authority !== null) {
      startWithAccessCodeEntry(authority, { resetDismissed: true });
      return;
    }

    void openWalletSelector();
  };

  const handleDisconnect = async (): Promise<void> => {
    setDisconnecting(true);
    try {
      await clearRiseSession();
      await wallet.disconnect();
      useAuthStore.getState().setUnauthenticated();
    } finally {
      setDisconnecting(false);
    }
  };

  const disabled =
    disconnecting ||
    wallet.connecting ||
    authStatus === "connecting" ||
    authStatus === "signing" ||
    activationStatus === "checking";
  const error = authError ?? activationError;
  const showDisconnect =
    activationStatus === "notActivated" && authority !== null;

  return (
    <div className="flex flex-col gap-3">
      {error !== null && <Alert variant="error">{error}</Alert>}
      <ConnectedWalletIndicator
        address={authority}
        className="w-fit max-w-full"
      />
      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={handleClick}
          disabled={disabled}
          loading={disabled && wallet.connected && !disconnecting}
          className="w-fit"
        >
          {getButtonLabel(wallet.connecting, authStatus, activationStatus)}
        </Button>
        {showDisconnect && (
          <Button
            type="button"
            variant="ghost"
            size="lg"
            onClick={() => {
              void handleDisconnect();
            }}
            disabled={disconnecting}
            loading={disconnecting}
            className="w-fit"
          >
            Disconnect
          </Button>
        )}
      </div>
    </div>
  );
};
